'use client';
import { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { formatToIndianCurrency } from '@/_config';
import { OfferSidebarContent } from './OfferSidebarContent';

/* ─────────────────────────────────────────────────────────────────
   CompactOfferRow — summary row shown inside the main card
───────────────────────────────────────────────────────────────── */
function CompactOfferRow({ icon, iconBg, iconColor, label, badge }) {
    return (
        <div className="flex align-items-center gap-2 mt-2" style={{ padding: '0.45rem 0' }}>
            <div style={{
                width: '1.6rem', height: '1.6rem', flexShrink: 0, borderRadius: '50%',
                background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <i className={`pi ${icon}`} style={{ fontSize: '0.68rem', color: iconColor }} />
            </div>
            <span style={{ fontSize: '0.78rem', color: '#1e293b', flex: 1 }}>{label}</span>
            {badge && (
                <span style={{
                    fontSize: '0.66rem', fontWeight: 700,
                    background: '#dcfce7', color: '#15803d',
                    padding: '0.1rem 0.4rem', borderRadius: '999px',
                    border: '1px solid #bbf7d0', whiteSpace: 'nowrap',
                }}>
                    {badge}
                </span>
            )}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────
   OfferDetailsCard
───────────────────────────────────────────────────────────────── */
export function OfferDetailsCard({ storeConfig }) {
    const { discountForAll = 0, prepaidDiscount = 0, coupons = [], allowCOD = true } = storeConfig ?? {};
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= 767
    );
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth <= 767);
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    const hasOffers = discountForAll > 0 || prepaidDiscount > 0 || coupons.length > 0;
    if (!hasOffers) return null;

    return (
        <>
            {/* ── Compact Card ──────────────────────────────────── */}
            <div style={{
                border: '1px solid rgba(11,49,99,0.18)', borderRadius: '10px',
                background: 'white', overflow: 'hidden',
                marginTop: '0.75rem', marginBottom: '0.5rem',
            }}>
                {/* Clickable header */}
                <button
                    onClick={() => setSidebarOpen(true)}
                    style={{
                        width: '100%', border: 'none', cursor: 'pointer',
                        background: 'var(--primary-color)',
                        padding: '0.55rem 1rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        transition: 'opacity 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                        <i className="pi pi-gift" style={{ color: 'white', fontSize: '0.85rem' }} />
                        <span style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem' }}>
                            Available Offers
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{
                            color: 'white', fontSize: '0.68rem', fontWeight: 600,
                            background: 'rgba(255,255,255,0.2)',
                            padding: '0.1rem 0.4rem', borderRadius: '999px',
                        }}>
                            View all
                        </span>
                        <i className="pi pi-chevron-right" style={{ color: 'white', fontSize: '0.7rem' }} />
                    </div>
                </button>

                {/* Compact rows */}
                <div style={{ padding: '0.5rem 1rem 0.6rem' }} className= "mt-2">
                    {discountForAll > 0 && (
                        <CompactOfferRow
                            icon="pi-percentage"
                            iconBg="#dcfce7" iconColor="#16a34a"
                            label={`Discount for you — Save ${formatToIndianCurrency(discountForAll)}`}
                            badge={`Save ${formatToIndianCurrency(discountForAll)}`}
                        />
                    )}

                    {prepaidDiscount > 0 && (
                        <CompactOfferRow
                            icon="pi-credit-card"
                            iconBg="#dbeafe" iconColor="#3b82f6"
                            label={`Pay Online & Save — Extra ${formatToIndianCurrency(prepaidDiscount)} off`}
                            badge={`${formatToIndianCurrency(prepaidDiscount)} off`}
                        />
                    )}

                    {/* Coupon count row */}
                    {coupons.length > 0 && (
                        <>
                            {(discountForAll > 0 || prepaidDiscount > 0) && (
                                <div style={{ height: '1px', background: '#e2e8f0', margin: '0.3rem 0' }} />
                            )}
                            <button
                                onClick={() => setSidebarOpen(true)}
                                style={{
                                    width: '100%', border: 'none', background: 'transparent',
                                    cursor: 'pointer', padding: '0.45rem 0',
                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                    textAlign: 'left',
                                }}
                            >
                                <div style={{
                                    width: '1.6rem', height: '1.6rem', flexShrink: 0, borderRadius: '50%',
                                    background: 'rgba(11,49,99,0.08)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="pi pi-ticket" style={{ fontSize: '0.68rem', color: 'var(--primary-color)' }} />
                                </div>
                                <span style={{ fontSize: '0.78rem', color: 'var(--primary-color)', fontWeight: 600, flex: 1 }}>
                                    {coupons.length} coupon{coupons.length > 1 ? 's' : ''} available
                                </span>
                                <span style={{
                                    fontSize: '0.68rem', color: 'var(--primary-color)', fontWeight: 600,
                                    display: 'flex', alignItems: 'center', gap: '3px',
                                }}>
                                    Click to view
                                    <i className="pi pi-arrow-right" style={{ fontSize: '0.6rem' }} />
                                </span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* ── Sidebar (bottom sheet on mobile, right panel on desktop) ── */}
            <Sidebar
                visible={sidebarOpen}
                position={isMobile ? 'bottom' : 'right'}
                onHide={() => setSidebarOpen(false)}
                style={isMobile
                    ? { height: '85dvh', borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column' }
                    : { width: '360px', maxWidth: '94vw', display: 'flex', flexDirection: 'column' }
                }
                pt={{
                    header:  { style: { padding: '0', background: 'var(--surface-100, #f8fafc)', flexShrink: 0 } },
                    content: { style: { padding: '0', overflowY: 'auto', flex: '1 1 0', minHeight: 0, WebkitOverflowScrolling: 'touch' } },
                }}
                header={
                    <div style={{
                        background: 'var(--surface-100, #f8fafc)',
                        borderBottom: '1px solid var(--surface-200, #e2e8f0)',
                        padding: '12px 16px',
                        display: 'flex', alignItems: 'center', gap: '10px',
                        width: '100%',
                    }}>
                        <div style={{
                            width: '30px', height: '30px', borderRadius: '8px', flexShrink: 0,
                            background: 'var(--primary-color)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <i className="pi pi-gift" style={{ color: '#fff', fontSize: '0.85rem' }} />
                        </div>
                        <div>
                            <div style={{ color: 'var(--primary-color)', fontWeight: 800, fontSize: '0.95rem' }}>
                                Available Offers
                            </div>
                            <div style={{ color: '#64748b', fontSize: '0.7rem' }}>
                                Coupons, discounts &amp; payment info
                            </div>
                        </div>
                    </div>
                }
            >
                <div style={{ padding: '1.2rem 1rem 0' }}>
                    {/* Checkout hint note */}
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.7rem 0.85rem',
                        borderRadius: '10px',
                        background: 'rgba(99,102,241,0.06)',
                        border: '1px solid rgba(99,102,241,0.2)',
                        display: 'flex', alignItems: 'flex-start', gap: '0.55rem',
                    }}>
                        <i className="pi pi-info-circle" style={{ color: '#6366f1', fontSize: '0.85rem', marginTop: '0.1rem', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.55 }}>
                            You can also view these coupon &amp; offer details on the{' '}
                            <strong style={{ color: '#6366f1' }}>Checkout page</strong>,
                            where you can apply coupons and calculate your final discounted price before placing the order.
                        </span>
                    </div>

                    <OfferSidebarContent
                        discountForAll={discountForAll}
                        prepaidDiscount={prepaidDiscount}
                        coupons={coupons}
                        allowCOD={allowCOD}
                    />
                </div>
            </Sidebar>
        </>
    );
}
